import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Save, RotateCcw, RefreshCcw, Eye, PenSquare,
  Wand2, Ban, PlayCircle, Loader2, ChevronDown, BookOpen, X, ChevronLeft, Plus, Replace,
} from "lucide-react";

/**
 * Prompt Studio — super_admin editor for the AI Studio tool prompts.
 *
 * Layout: left rail lists tools, right side edits the selected tool's
 * System Prompt, User Prompt Template ({{variable}} interpolation), and
 * a "Negative tail" appended to every rendered image prompt for that
 * tool. A live preview button renders the template against the tool's
 * built-in sample input so admins can eyeball the merged text before
 * publishing.
 */
export default function AdminPromptStudio() {
  const [tools, setTools] = useState([]);
  const [params, setParams] = useSearchParams();
  const activeSlug = params.get("tool");
  const [selected, setSelected] = useState(null); // {baseline, override}
  const [draft, setDraft] = useState({ system: "", template: "", negative_tail: "", knowledge_base: "", notes: "" });
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [showBaseline, setShowBaseline] = useState(false);
  // KB starter templates + picker dialog state.
  const [kbTemplates, setKbTemplates] = useState([]);
  const [kbCategories, setKbCategories] = useState([]);
  const [kbPickerOpen, setKbPickerOpen] = useState(false);
  const [kbPreview, setKbPreview] = useState(null); // template currently expanded

  // Load the whole catalog once so the left rail knows every tool.
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/admin/prompt-studio");
        setTools(data.tools || []);
        if (!activeSlug && data.tools?.[0]) {
          setParams({ tool: data.tools[0].baseline.slug });
        }
      } catch (err) {
        toast.error(formatError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line

  // Load starter KB templates once (universal + tool-tagged). Filtered
  // client-side when the picker opens so we only fetch once per session.
  useEffect(() => {
    api.get("/admin/prompt-studio/kb-templates").then((r) => {
      setKbTemplates(r.data?.templates || []);
      setKbCategories(r.data?.categories || []);
    }).catch(() => { /* non-fatal — picker just shows empty state */ });
  }, []);

  // Load the selected tool's full detail when the URL slug changes.
  useEffect(() => {
    if (!activeSlug) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/prompt-studio/${activeSlug}`);
        setSelected(data);
        const d = {
          system:         data.override.system || "",
          template:       data.override.template || "",
          negative_tail:  data.override.negative_tail || "",
          knowledge_base: data.override.knowledge_base || "",
          notes:          data.override.notes || "",
        };
        setDraft(d);
        setInitial(d);
        setPreview(null);
      } catch (err) {
        toast.error(formatError(err));
      }
    })();
  }, [activeSlug]);

  const dirty = useMemo(() => (
    initial && JSON.stringify(draft) !== JSON.stringify(initial)
  ), [draft, initial]);

  // Refresh the left-rail catalog so the "EDITED" badge reflects the
  // latest persisted state after Save / Reset. Cheap — one small GET.
  const refreshCatalog = async () => {
    try {
      const { data } = await api.get("/admin/prompt-studio");
      setTools(data.tools || []);
    } catch { /* non-fatal — the rail keeps its previous state */ }
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/admin/prompt-studio/${selected.baseline.slug}`, draft);
      toast.success("Prompt saved — next generation uses these settings.");
      setInitial(draft);
      await refreshCatalog();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!selected) return;
    if (!window.confirm(`Reset ${selected.baseline.display_name} to its built-in defaults? Any admin overrides for this tool are dropped.`)) return;
    try {
      await api.post(`/admin/prompt-studio/${selected.baseline.slug}/reset`);
      toast.success("Reset to defaults.");
      const { data } = await api.get(`/admin/prompt-studio/${selected.baseline.slug}`);
      setSelected(data);
      const d = { system: "", template: "", negative_tail: "", knowledge_base: "", notes: "" };
      setDraft(d); setInitial(d); setPreview(null);
      await refreshCatalog();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  const revert = () => { if (initial) setDraft(initial); };

  const runPreview = async () => {
    if (!selected) return;
    setPreviewing(true);
    try {
      const { data } = await api.post(
        `/admin/prompt-studio/${selected.baseline.slug}/preview`,
        {
          system: draft.system, template: draft.template,
          negative_tail: draft.negative_tail,
          knowledge_base: draft.knowledge_base,
          sample: selected.baseline.sample_input || {},
        },
      );
      setPreview(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setPreviewing(false);
    }
  };

  if (loading) {
    return (
      <AppShell title="Prompt Studio">
        <div className="max-w-5xl"><p className="text-sm text-muted-foreground">Loading tools…</p></div>
      </AppShell>
    );
  }

  return (
    <AppShell title="Prompt Studio">
      <div className="w-full">
        <PageHero
          eyebrow="AI Studio · super admin"
          icon={PenSquare}
          title={<>Prompt <span className="text-grad">Studio</span></>}
          subtitle="Edit the system prompt, user prompt template, and negative-prompt tail powering every AI Studio tool. Changes go live on the next generation — no redeploy."
          testid="prompt-studio-hero"
        />

        <div className="grid gap-6 lg:grid-cols-[16rem_1fr]">
          {/* Left rail */}
          <aside className="space-y-1" data-testid="prompt-studio-rail">
            {tools.map((t) => {
              const slug = t.baseline.slug;
              const overrode = !!(t.override.system || t.override.template || t.override.negative_tail || t.override.knowledge_base);
              const isActive = slug === selected?.baseline.slug;
              return (
                <button
                  key={slug}
                  onClick={() => setParams({ tool: slug })}
                  data-testid={`prompt-studio-tool-${slug}`}
                  className={[
                    "w-full text-left px-3 py-2 rounded-lg border text-sm transition",
                    isActive ? "border-primary bg-primary/5 shadow-sm"
                             : "border-border bg-white hover:bg-muted/50",
                  ].join(" ")}
                >
                  <span className="flex items-center justify-between">
                    <span className="font-semibold">{t.baseline.display_name}</span>
                    {overrode && (
                      <span className="text-[9.5px] uppercase tracking-widest font-bold text-pink-600 bg-pink-100/60 rounded-full px-1.5 py-0.5">
                        edited
                      </span>
                    )}
                  </span>
                  <span className="block text-[10.5px] text-muted-foreground mt-0.5">{slug}</span>
                </button>
              );
            })}
          </aside>

          {/* Editor */}
          {selected ? (
            <div className="space-y-6" data-testid="prompt-studio-editor">
              {/* Header actions */}
              <div className="surface p-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                    Editing
                  </p>
                  <p className="font-display text-xl">{selected.baseline.display_name}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={reset}
                    data-testid="prompt-studio-reset"
                    className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted"
                  >
                    <RefreshCcw className="w-3.5 h-3.5" /> Reset to defaults
                  </button>
                  {dirty && (
                    <>
                      <button onClick={revert} disabled={saving} data-testid="prompt-studio-revert"
                        className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50">
                        <RotateCcw className="w-3.5 h-3.5" /> Revert
                      </button>
                      <button onClick={save} disabled={saving} data-testid="prompt-studio-save"
                        className="btn-amber inline-flex items-center gap-1 text-xs disabled:opacity-60">
                        <Save className="w-3.5 h-3.5" /> {saving ? "Saving…" : "Save changes"}
                      </button>
                    </>
                  )}
                </div>
              </div>

              <Panel icon={BookOpen} title="Knowledge base"
                     subtitle="Admin-curated reference brief prepended to the system prompt on every generation. Use this for brand voice, factual guardrails, banned words, competitor context, and non-negotiable style rules. Plain text or markdown — up to 20,000 characters.">
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setKbPickerOpen(true)}
                    data-testid="prompt-studio-kb-templates-open"
                    className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 hover:bg-primary/10 text-primary px-3 py-1.5 text-xs font-semibold"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Insert from starter library
                  </button>
                  {draft.knowledge_base && (
                    <button
                      type="button"
                      onClick={() => setDraft({ ...draft, knowledge_base: "" })}
                      data-testid="prompt-studio-kb-clear"
                      className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <textarea
                  rows={10}
                  value={draft.knowledge_base}
                  onChange={(e) => setDraft({ ...draft, knowledge_base: e.target.value })}
                  placeholder="e.g. Brand voice: earnest, precise, never hypey. Always spell CPG in caps. Never suggest artificial colors or ingredients not on the approved list…"
                  data-testid="prompt-studio-knowledge"
                  maxLength={20000}
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:border-primary/50"
                />
                <p className="text-[10.5px] text-muted-foreground">
                  {(draft.knowledge_base || "").length.toLocaleString()} / 20,000 characters
                </p>
              </Panel>

              <Panel icon={Sparkles} title="System prompt"
                     subtitle="Base persona + rules for the LLM that writes the design brief. Leave blank to use the baseline.">
                <textarea
                  rows={14}
                  value={draft.system}
                  onChange={(e) => setDraft({ ...draft, system: e.target.value })}
                  placeholder="(using baseline)"
                  data-testid="prompt-studio-system"
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-[13px] font-mono leading-relaxed focus:outline-none focus:border-primary/50"
                />
                <BaselineToggle label="View built-in system prompt"
                  content={selected.baseline.system}
                />
              </Panel>

              <Panel icon={Wand2} title="User prompt template"
                     subtitle={`Rendered per generation. Use {{variable}} to interpolate any input field. Leave blank to use the code-authored builder.`}>
                <textarea
                  rows={12}
                  value={draft.template}
                  onChange={(e) => setDraft({ ...draft, template: e.target.value })}
                  placeholder="(using baseline — leave blank to keep code-authored builder)"
                  data-testid="prompt-studio-template"
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-[13px] font-mono leading-relaxed focus:outline-none focus:border-primary/50"
                />
                <VariableChips vars={selected.baseline.variables || []}
                  onInsert={(v) => setDraft((d) => ({
                    ...d, template: `${d.template}${d.template ? "\n" : ""}${v}: {{${v}}}`,
                  }))}
                />
              </Panel>

              <Panel icon={Ban} title="Negative tail"
                     subtitle="Appended to every image prompt for this tool as 'Avoid: …'. Great place to fix recurring artifacts (phantom text, extra fingers, melted geometry, etc.).">
                <textarea
                  rows={3}
                  value={draft.negative_tail}
                  onChange={(e) => setDraft({ ...draft, negative_tail: e.target.value })}
                  placeholder="e.g. no phantom text, no melted geometry, no oversized crown or ribbons"
                  data-testid="prompt-studio-negative"
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-[13px] font-mono leading-relaxed focus:outline-none focus:border-primary/50"
                />
              </Panel>

              <Panel icon={PenSquare} title="Notes for future editors" subtitle="Admin-only. Doesn't leave the DB.">
                <textarea
                  rows={3}
                  value={draft.notes}
                  onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                  placeholder="Why did you change this? What worked / didn't?"
                  data-testid="prompt-studio-notes"
                  className="w-full rounded-lg border border-border bg-white px-3 py-2 text-[13px] leading-relaxed focus:outline-none focus:border-primary/50"
                />
              </Panel>

              {/* Preview */}
              <Panel icon={Eye} title="Preview render" subtitle={`Renders the current draft against a sample input (${_summariseSample(selected.baseline.sample_input)}).`}>
                <button
                  onClick={runPreview}
                  disabled={previewing}
                  data-testid="prompt-studio-preview"
                  className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
                >
                  {previewing
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Rendering…</>
                    : <><PlayCircle className="w-3.5 h-3.5" /> Preview against sample</>}
                </button>
                {preview && (
                  <div className="mt-4 space-y-3 text-[13px]" data-testid="prompt-studio-preview-output">
                    {preview.knowledge_base && (
                      <PreviewBlock
                        label="Knowledge base prepended (verbatim)"
                        body={preview.knowledge_base}
                      />
                    )}
                    <PreviewBlock
                      label="Effective system prompt sent to LLM (first 600 chars)"
                      body={(preview.effective_system || preview.system || "").slice(0, 600) + ((preview.effective_system || preview.system)?.length > 600 ? "…" : "")}
                    />
                    <PreviewBlock label="Rendered user prompt" body={preview.user_prompt || "(empty — the code-authored builder will be used at runtime.)"} />
                    {preview.negative_tail && (
                      <PreviewBlock label="Negative tail appended" body={"Avoid: " + preview.negative_tail} />
                    )}
                    <p className="text-[10.5px] text-muted-foreground">
                      Variables you referenced: <b>{preview.variables_used.join(", ") || "(none)"}</b>
                      <br />
                      Variables available: <span className="font-mono">{(preview.variables_available || []).join(", ")}</span>
                    </p>
                  </div>
                )}
              </Panel>

              {selected.baseline.fallback_note && (
                <p className="text-[11.5px] text-muted-foreground italic">
                  {selected.baseline.fallback_note}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Select a tool to edit its prompts.</p>
          )}
        </div>
      </div>

      {kbPickerOpen && (
        <KbTemplatePicker
          templates={kbTemplates}
          categories={kbCategories}
          toolSlug={selected?.baseline?.slug}
          currentKB={draft.knowledge_base}
          preview={kbPreview}
          setPreview={setKbPreview}
          onClose={() => { setKbPickerOpen(false); setKbPreview(null); }}
          onInsert={(tpl, mode) => {
            const existing = draft.knowledge_base || "";
            let next;
            if (mode === "replace") next = tpl.body;
            else {
              const sep = existing.trim() ? "\n\n" : "";
              next = existing + sep + tpl.body;
            }
            // Respect the 20k ceiling.
            if (next.length > 20000) next = next.slice(0, 20000);
            setDraft({ ...draft, knowledge_base: next });
            setKbPickerOpen(false);
            setKbPreview(null);
            toast.success(mode === "replace"
              ? `Replaced with "${tpl.name}".`
              : `Added "${tpl.name}" to Knowledge base.`);
          }}
        />
      )}
    </AppShell>
  );
}

function Panel({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="surface p-5">
      <div className="flex items-center gap-2 pb-2 border-b border-shell-border/60">
        {Icon && <Icon className="w-4 h-4 text-primary" />}
        <p className="font-display text-lg">{title}</p>
      </div>
      {subtitle && <p className="text-[11.5px] text-muted-foreground mt-2 leading-relaxed">{subtitle}</p>}
      <div className="mt-3 space-y-2">{children}</div>
    </div>
  );
}

function BaselineToggle({ label, content }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary mt-1 transition"
      >
        <ChevronDown className={["w-3 h-3 transition-transform", open ? "rotate-180" : ""].join(" ")} />
        {label}
      </button>
      {open && (
        <pre className="mt-2 max-h-64 overflow-auto rounded-lg border border-shell-border/60 bg-muted/30 p-3 text-[11.5px] font-mono whitespace-pre-wrap leading-relaxed">
          {content}
        </pre>
      )}
    </div>
  );
}

function VariableChips({ vars, onInsert }) {
  if (!vars.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      <span className="text-[10.5px] uppercase tracking-widest font-semibold text-muted-foreground mr-1">
        Variables:
      </span>
      {vars.map((v) => (
        <button
          key={v}
          onClick={() => onInsert(v)}
          data-testid={`prompt-studio-var-${v}`}
          title={`Insert {{${v}}}`}
          className="inline-flex items-center gap-1 rounded-md border border-shell-border bg-white px-1.5 py-0.5 text-[10.5px] font-mono text-foreground hover:bg-primary hover:text-white transition"
        >
          {v}
        </button>
      ))}
    </div>
  );
}

function PreviewBlock({ label, body }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">{label}</p>
      <pre className="rounded-lg border border-shell-border/60 bg-muted/30 p-3 font-mono text-[12px] whitespace-pre-wrap leading-relaxed max-h-72 overflow-auto">
        {body}
      </pre>
    </div>
  );
}

function _summariseSample(sample) {
  if (!sample || typeof sample !== "object") return "no sample";
  const parts = [];
  if (sample.product_name) parts.push(sample.product_name);
  if (sample.brand_name) parts.push(sample.brand_name);
  if (sample.category)   parts.push(sample.category);
  return parts.slice(0, 3).join(" · ") || "sample input";
}

// ---------------------------------------------------------------------------
// Knowledge Base Starter Template picker — modal dialog that lets admins
// browse curated briefs, expand any for a full preview, then Append/Replace
// the current KB. Filters to templates flagged for the currently-selected
// tool (or universal templates with no tool tag).
// ---------------------------------------------------------------------------
function KbTemplatePicker({
  templates, categories, toolSlug, currentKB,
  preview, setPreview, onClose, onInsert,
}) {
  // Filter to universal templates + those tagged for the current tool.
  const filtered = React.useMemo(() => {
    if (!toolSlug) return templates;
    return templates.filter((t) => {
      const applicable = t.tools || [];
      return applicable.length === 0 || applicable.includes(toolSlug);
    });
  }, [templates, toolSlug]);

  const grouped = React.useMemo(() => {
    const map = {};
    for (const t of filtered) {
      (map[t.category] = map[t.category] || []).push(t);
    }
    return map;
  }, [filtered]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="prompt-studio-kb-picker"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-shell-border/70">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-primary" />
              <p className="font-display text-lg">Knowledge Base starter library</p>
            </div>
            <p className="text-[11.5px] text-muted-foreground mt-0.5">
              {filtered.length} template{filtered.length === 1 ? "" : "s"} for {toolSlug || "any tool"}. Click one to preview, then Append or Replace.
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="prompt-studio-kb-picker-close"
            className="p-1.5 rounded-full hover:bg-muted"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {preview ? (
          <div className="flex-1 overflow-hidden flex flex-col">
            <div className="px-5 py-3 border-b border-shell-border/70 flex items-center justify-between gap-3">
              <button
                onClick={() => setPreview(null)}
                data-testid="prompt-studio-kb-picker-back"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="w-3.5 h-3.5" /> Back to library
              </button>
              <div className="flex items-center gap-2">
                {currentKB && currentKB.trim() && (
                  <button
                    onClick={() => onInsert(preview, "replace")}
                    data-testid="prompt-studio-kb-picker-replace"
                    className="inline-flex items-center gap-1 rounded-full border border-shell-border px-3 py-1.5 text-xs hover:bg-muted"
                  >
                    <Replace className="w-3.5 h-3.5" /> Replace
                  </button>
                )}
                <button
                  onClick={() => onInsert(preview, "append")}
                  data-testid="prompt-studio-kb-picker-append"
                  className="btn-amber inline-flex items-center gap-1 text-xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {currentKB && currentKB.trim() ? "Append to KB" : "Use this template"}
                </button>
              </div>
            </div>
            <div className="p-5 overflow-y-auto">
              <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                {preview.category}
              </p>
              <h3 className="font-display text-xl mt-0.5">{preview.name}</h3>
              <p className="text-sm text-muted-foreground mt-1">{preview.summary}</p>
              <pre
                className="mt-4 whitespace-pre-wrap font-mono text-[12.5px] leading-relaxed bg-muted/50 rounded-lg p-4 border border-shell-border/60"
                data-testid="prompt-studio-kb-picker-preview-body"
              >
                {preview.body}
              </pre>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5">
            {filtered.length === 0 ? (
              <p className="text-sm text-muted-foreground italic">
                No starter templates yet for this tool. Templates without a tool tag are always shown here.
              </p>
            ) : (
              (categories || []).map((cat) => {
                const items = grouped[cat] || [];
                if (!items.length) return null;
                return (
                  <div key={cat} className="mb-6">
                    <p className="text-[10.5px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
                      {cat}
                    </p>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {items.map((tpl) => (
                        <button
                          key={tpl.id}
                          onClick={() => setPreview(tpl)}
                          data-testid={`prompt-studio-kb-template-${tpl.id}`}
                          className="text-left rounded-lg border border-shell-border/70 p-3 hover:border-primary/60 hover:bg-primary/5 transition"
                        >
                          <p className="font-semibold text-sm">{tpl.name}</p>
                          <p className="text-[11.5px] text-muted-foreground mt-0.5 leading-relaxed">
                            {tpl.summary}
                          </p>
                          <p className="text-[10.5px] uppercase tracking-widest text-muted-foreground mt-2">
                            {(tpl.body || "").length.toLocaleString()} chars
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

